import React, { useCallback, useEffect, useState } from "react";
import { defineMessages, FormattedMessage } from "react-intl";
import { defaultFormMessages } from "../shared/crud/form";
import { defaultListMessages } from "../shared/crud/list";
import styled from "styled-components";
import { SectionHeading } from "../../components/section-heading";
import { Alert, Button, Card } from "react-bootstrap";
import { useDropzone } from "react-dropzone";
import { AxiosResponse } from "axios";
import axios from "http/customAxios"
import { FullWidthLoader } from "../../components/full-width-loader";
import { IConfig, useConfig } from "../../lib/config";
import { createFileDownloadFromRawData } from "../../lib/supportFunctions";
import { format } from "date-fns";
import Highlight from "react-highlight";
import { withAuthorizationVerifier } from "../../components/authorization-verifier";
import { useKeycloak } from "../../components/keycloak-provider";

export enum UserManagerActionType {
  CREATE = "CREATE",
  EDIT = "EDIT",
  DELETE = "DELETE",
}

export interface UserManagerProps {
  action: UserManagerActionType;
}

interface OperationError {
  message: string;
}

export interface AsyncUserManagerResponseDto {
  message: string;
  errors: UserManagerResult[];
  results: AsyncUserManagerResult[];
}

export interface UserManagerResponseDto {
  message: string;
  errors: UserManagerResult[];
  results: UserManagerResult[];
}

export interface UserManagerResult {
  message: string;
  index: number;
  data: Data;
}

export interface AsyncUserManagerResult {
  message: string;
  index: number;
  data: AsyncData;
}

export interface AsyncData {
  total: number;
}

export interface Data {
  user: string;
  property: string;
  groups: string[];
  existingUser: boolean;
  legacyChanged: boolean;
  keyCloakChanged: boolean;
  password?: string;
}

const messages = {
  ...defaultFormMessages,
  ...defaultListMessages,
  ...defineMessages({
    createTitle: {
      id: "internalmenu.users.title.batchCreate",
      defaultMessage: "Create users with excel file",
    },
    editTitle: {
      id: "internalmenu.users.title.batchEdit",
      defaultMessage: "Edit users with excel file",
    },
    deleteTitle: {
      id: "internalmenu.users.title.batchDelete",
      defaultMessage: "Delete users with excel file",
    },
    description: {
      id: "inventoryMultiUpdate.description",
      defaultMessage: "Update inventory",
    },
    import: {
      id: "inventoryMultiUpdate.import",
      defaultMessage: "Import",
    },
    exportToExcel: {
      id: "userManager.exportToExcel",
      defaultMessage: "Download template",
    },
    errorsFound: {
      id: "userManager.errorsFound",
      defaultMessage: "rows with errors were found",
    },
    rawResult: {
      id: "userManager.rawResult",
      defaultMessage: "Raw result",
    },
    processedSuccessfully: {
      id: "userManager.processedSuccessfully",
      defaultMessage: "Processed successfully",
    },
    row: {
      id: "userManager.row",
      defaultMessage: "Row",
    },
    reason: {
      id: "userManager.reason",
      defaultMessage: "Reason",
    },
    failed: {
      id: "userManager.failed",
      defaultMessage: "failed",
    },
    createdUsers: {
      id: "userManager.createdUsers",
      defaultMessage: "Created users",
    },
    asyncProcessing: {
      id: "userManager.asyncProcessing",
      defaultMessage: "Processed asynchronously",
    },
    password: {
      id: "userManager.password",
      defaultMessage: "Password",
    },
    user: {
      id: "userManager.user",
      defaultMessage: "User",
    },
    dropHere: {
      id: "app.imageManager.dropHere",
      defaultMessage: "Drop files here...",
    },
  }),
};

const Container = styled.div`
  display: flex;
  flex-direction: column;
  margin: 1rem;
  > * {
    margin: 1rem 0;
  }
`;
const ButtonContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  margin: 1rem;
  > * {
    margin: 0 5px;
  }
  :last-child {
    margin-right: 5px;
  }
`;

const DropzoneContainer = styled.div`
  width: auto;
  height: 100px;
  border: 2px dashed #dae6e8;
  background-color: ${({ theme }) => theme.greyBackground};;
  font-size: 21px;
  display: flex;
  justify-content: center;
  align-items: center;
  cursor: pointer;
  margin-bottom: 25px;
  outline: none;
`;

const ResultContainer = styled.div`
  margin: 16px;
`;

const ResultItemContainer = styled.div`
  padding: 16px;
  border: 1px solid #dae6e8;
`;

const StyledHighlight = styled(Highlight)`
  font-size: 12px;
`;

const HighlightContainer = styled.div`
  display: flex;
  justify-content: center;
  background-color: #f7f7f7;
  padding: 20px;
`;

function isAsyncResult(it: unknown): it is AsyncUserManagerResponseDto {
  return (
    (it as AsyncUserManagerResponseDto).results.length === 1 &&
    (it as AsyncUserManagerResponseDto).results[0].data.total !== undefined
  );
}

function UserManagerResultRenderer({
  action,
  result,
}: {
  action: UserManagerActionType;
  result: UserManagerResponseDto | AsyncUserManagerResponseDto;
}) {
  if (action !== UserManagerActionType.CREATE && !result.results.length) {
    return null;
  }
  if (isAsyncResult(result)) {
    return (
      <Alert variant="info">
        <strong>
          <FormattedMessage {...messages.asyncProcessing} />:
        </strong>
      </Alert>
    );
  }
  return (
    <>
      <Alert variant="info">
        <strong>
          {result.results.length} <FormattedMessage {...messages.createdUsers} />:
        </strong>
      </Alert>
      <div>
        {result.results.map(it => (
          <ResultItemContainer>
            <div>
              <b>
                <FormattedMessage {...messages.row} />
              </b>
              {it.index}.
            </div>
            <div>
              <b>
                <FormattedMessage {...messages.user} />
              </b>
              {it.data.user}.
            </div>
            <div>
              <b>
                <FormattedMessage {...messages.password} />
              </b>
              : {it.data.password}
            </div>
          </ResultItemContainer>
        ))}
      </div>
    </>
  );
}

function UserManager({ action }: UserManagerProps) {
  const configuration: IConfig = useConfig();
  const keycloak = useKeycloak();
  const [file, setFile] = useState<File | undefined>(undefined);
  const [isLoading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<OperationError | null | undefined>(undefined);
  const [errorResults, setErrorResults] = useState<UserManagerResult[] | undefined>();
  const [result, setResult] = useState<
    UserManagerResponseDto | AsyncUserManagerResponseDto | undefined
  >(undefined);

  const onDrop = useCallback(<T extends File>(acceptedFiles: T[]) => {
    if (!acceptedFiles || acceptedFiles.length === 0) {
      return;
    }
    setFile(acceptedFiles[0]);
  }, []);
  const { getRootProps, getInputProps } = useDropzone({ onDrop });

  const getUserManagerEndpointMethodByAction = (action: UserManagerActionType) => {
    switch (action) {
      case UserManagerActionType.CREATE:
        return axios.post;
      case UserManagerActionType.EDIT:
      case UserManagerActionType.DELETE:
        return axios.put;
      default:
        throw new Error("unable to get user manager request method on action given");
    }
  };

  const getUserManagerEndpointUrlByAction = useCallback((action: UserManagerActionType) => {
    const baseUrl: string = configuration.userManagerUrl;
    switch (action) {
      case UserManagerActionType.CREATE:
        return `${baseUrl}/create-example`;
      case UserManagerActionType.EDIT:
        return `${baseUrl}/update-example`;
      case UserManagerActionType.DELETE:
        return `${baseUrl}/delete-example`;
      default:
        throw new Error("unable to get user manager request url on action given");
    }
  }, [configuration.userManagerUrl]);

  const getUserManagerUploadEndpointByAction = useCallback((action: UserManagerActionType) => {
    const baseUrl: string = configuration.userManagerUrl;
    switch (action) {
      case UserManagerActionType.CREATE:
      case UserManagerActionType.EDIT:
        return `${baseUrl}/xlsx`;
      case UserManagerActionType.DELETE:
        return `${baseUrl}/delete-xlsx`;
      default:
        throw new Error("unable to get user manager request url on action given");
    }
  }, [configuration.userManagerUrl]);

  const handleImport = useCallback(async () => {
    if (!file) {
      return;
    }
    const formData = new FormData();
    formData.append("file-excel", file);
    setLoading(true);
    setError(undefined);
    setErrorResults(undefined);
    try {
      const method = getUserManagerEndpointMethodByAction(action);
      const uploadUrl = getUserManagerUploadEndpointByAction(action);
      const cookies = document.cookie;
      const response: AxiosResponse<
        UserManagerResponseDto | AsyncUserManagerResponseDto
      > = await method(uploadUrl, formData, {
        headers: {
          "content-type": "multipart/form-data",
          "backoffice-ng-cookie": cookies,
          Authorization: keycloak.token,
        },
      });
      if (response.data.errors?.length) {
        setErrorResults(response.data.errors);
      }
      setResult(response.data);
    } catch (err) {
      setError({ message: "Access denied" });
    }
    setLoading(false);
  }, [action, file, getUserManagerUploadEndpointByAction, keycloak.token]);

  const handleExport = useCallback(async () => {
    const url = getUserManagerEndpointUrlByAction(action);
    const response = await axios.get(url, {
      responseType: "blob",
      headers: {
        Authorization: keycloak.token,
      }
    });
    createFileDownloadFromRawData(
      response.data,
      `user_manager_${format(new Date(), "yyyyMMdd")}.xlsx`,
      "application/vnd.openxmlformats"
    );
  }, [action, getUserManagerEndpointUrlByAction, keycloak.token]);

  const getComponentTitleByAction = useCallback((action: UserManagerActionType) => {
    switch (action) {
      case UserManagerActionType.CREATE:
        return messages.createTitle;
      case UserManagerActionType.EDIT:
        return messages.editTitle;
      case UserManagerActionType.DELETE:
        return messages.deleteTitle;
      default:
        return "";
    }
  }, []);

  if (isLoading) {
    return <FullWidthLoader />;
  }

  return (
    <>
      <SectionHeading>
        <FormattedMessage {...getComponentTitleByAction(action)} />
      </SectionHeading>
      <Card className="ml-4 mr-4 mb-4 mt-4">
        <Container>
          <div>
            <FormattedMessage {...messages.description} />
          </div>
          <DropzoneContainer {...getRootProps()}>
            <input {...getInputProps()} />
            {file ? <div>{file.name}</div> : <FormattedMessage {...messages.dropHere} />}
          </DropzoneContainer>
        </Container>
        {error && (
          <Alert variant="danger">
            <strong>
              <FormattedMessage {...messages.saveError} />
            </strong>
          </Alert>
        )}
        <ButtonContainer>
          <Button variant="primary" type="button" onClick={handleImport}>
            <FormattedMessage {...messages.import} />
          </Button>
          <Button variant="primary" type="button" onClick={handleExport}>
            <FormattedMessage {...messages.exportToExcel} />
          </Button>
        </ButtonContainer>
        {result && (
          <ResultContainer>
            <Alert variant="success">
              <strong>
                <FormattedMessage {...messages.processedSuccessfully} />
              </strong>
            </Alert>
            <UserManagerResultRenderer action={action} result={result} />
            {errorResults ? (
              <>
                <Alert variant="danger">
                  <strong>
                    {errorResults.length} <FormattedMessage {...messages.errorsFound} />:
                  </strong>
                </Alert>
                <div>
                  {errorResults.map(it => (
                    <ResultItemContainer>
                      <div>
                        <b>
                          <FormattedMessage {...messages.row} />
                        </b>{" "}
                        {it.index} failed.
                      </div>
                      <div>
                        <b>
                          <FormattedMessage {...messages.reason} />
                        </b>
                        : {it.message}
                      </div>
                    </ResultItemContainer>
                  ))}
                </div>
              </>
            ) : null}
            <hr />
            <div>
              <FormattedMessage {...messages.rawResult} />:
            </div>
            <HighlightContainer>
              <StyledHighlight className="json">{JSON.stringify(result, null, 2)}</StyledHighlight>
            </HighlightContainer>
          </ResultContainer>
        )}
      </Card>
    </>
  );
}

export default withAuthorizationVerifier(UserManager);
